import { ExternalLink } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { CatalogItemSourceView } from '@/api/types'
import type { PriceTier } from '@/features/projects/rollup-bom'

interface SourceAttributionBadgeProps {
  /** Phase 16: which tier the row's price actually resolved at. */
  tier: PriceTier
  /** Live source row (only meaningful when {@code tier === 'live'}). */
  liveSource?: CatalogItemSourceView | null
  /** Mark the badge stale when the live source's last fetch is past the staleness threshold. */
  isStale?: boolean
  className?: string
}

const tierLabel: Record<PriceTier, string> = {
  locked: 'Locked',
  live: 'Live',
  default: 'Listed',
}

const tierClass: Record<PriceTier, string> = {
  locked: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100',
  live: 'bg-sky-100 text-sky-900 dark:bg-sky-900/40 dark:text-sky-100',
  default: 'bg-muted text-muted-foreground',
}

/**
 * Phase 16 — small chip rendered next to a BOM line / catalog row's price. Indicates which tier
 * the price came from and (for {@code live}) which source + whether the fetch is stale.
 *
 * <p>The chip is interactive only for {@code live}: clicking opens the source URL in a new tab so
 * the Doer can sanity-check current retailer pricing. Other tiers render as a static label.
 */
export const SourceAttributionBadge = ({
  tier,
  liveSource,
  isStale = false,
  className,
}: SourceAttributionBadgeProps) => {
  const label = tierLabel[tier]
  const base = cn(
    'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide',
    tierClass[tier],
    isStale && 'opacity-70',
    className
  )

  if (tier === 'live' && liveSource) {
    return (
      <a
        href={liveSource.sourceUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(base, 'hover:underline')}
        title={
          isStale
            ? `Stale price from ${liveSource.sourceDisplayName} — last fetched ${liveSource.lastFetchedAt}`
            : `Live price from ${liveSource.sourceDisplayName}`
        }
      >
        <span>
          {label} · {liveSource.sourceDisplayName}
        </span>
        <ExternalLink className="h-2.5 w-2.5" aria-hidden />
        {isStale && <span className="sr-only">stale</span>}
      </a>
    )
  }

  return <span className={base}>{label}</span>
}
